// The tenant recovery file: how one is written, and how one is read back.
//
// Lifted out of `vault-unlock.ts` because several callers need it and
// must behave identically — `unlock()`, the raw-key ceremony
// (`vault-raw-key-ceremony.ts`), and the two places that PRODUCE a file
// (tenant-key creation/rotation, and the export added by ADR 0009). The
// public-key check in particular is not negotiable and must not drift
// between readers, so it exists exactly once, here; and now that a file
// can be produced in more than one place, so does the format.
import { b64u, unb64u } from '@stoaria/vault-core'

interface RecoveryFile {
  v: 1
  tenantPrivateKey: string
  tenantPublicKey: string
}

/**
 * Renders a keypair as the recovery file an operator stores offline.
 *
 * Pretty-printed, because this is a file a human saves, opens to check,
 * and occasionally has to read a fingerprint out of over a call. Two
 * spaces of indentation cost nothing and the alternative is a single
 * 300-character line.
 *
 * SECURITY: the returned string IS the tenant private key. It exists so
 * it can be put on screen and downloaded, which is the one legitimate
 * reason for key material to become a string — every caller is a
 * deliberate "show the operator their recovery file" path and none of
 * them may log it.
 */
export function buildRecoveryFile(
  privateKey: Uint8Array,
  publicKey: Uint8Array,
): string {
  const file: RecoveryFile = {
    v: 1,
    tenantPrivateKey: b64u(privateKey),
    tenantPublicKey: b64u(publicKey),
  }
  return JSON.stringify(file, null, 2)
}

function parseRecoveryFile(text: string): RecoveryFile {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    throw new Error('recovery file is not valid JSON')
  }
  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    (parsed as { v?: unknown }).v !== 1 ||
    typeof (parsed as { tenantPrivateKey?: unknown }).tenantPrivateKey !==
      'string' ||
    typeof (parsed as { tenantPublicKey?: unknown }).tenantPublicKey !==
      'string'
  )
    throw new Error(
      'recovery file is missing v/tenantPrivateKey/tenantPublicKey',
    )
  return parsed as RecoveryFile
}

/**
 * Parses a pasted recovery file and returns its PRIVATE half as PKCS8
 * bytes — but only after its PUBLIC half matches `serverTenantPubkey`
 * exactly.
 *
 * That comparison is the only thing standing between an operator and
 * silently loading the recovery file of a *previous* tenant key (or
 * another tenant entirely). Without it the failure surfaces much later as
 * an unopenable secret or a daemon that cannot decrypt anything, with no
 * indication that the wrong file was pasted an hour earlier. It is a plain
 * string compare of base64url that both sides produce the same way, so
 * there is no normalisation to get wrong.
 *
 * SECURITY: the returned array is key material. The caller owns it and is
 * expected to zero it as soon as it has been imported or re-wrapped.
 */
export function privateKeyFromRecoveryFile(
  text: string,
  serverTenantPubkey: string,
): Uint8Array {
  const recovery = parseRecoveryFile(text)
  if (recovery.tenantPublicKey !== serverTenantPubkey)
    throw new Error(
      'recovery file public key mismatch — this is not the recovery ' +
        "file for this tenant's current key",
    )
  return unb64u(recovery.tenantPrivateKey)
}
